import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import LoadingSpinner from '../LoadingSpinner';

const { RangePicker } = DatePicker;

const SearchForm = ({
  searchForm,
  locationQuery,
  hotelQuery,
  locationSuggestions,
  hotelSuggestions,
  showSuggestions,
  showHotelSuggestions,
  loading,
  error,
  onLocationChange,
  onHotelNameChange,
  onInputChange,
  onSelectLocation,
  onSelectHotel,
  onSubmit,
  safeString,
  compact = false
}) => {
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [inputRef, setInputRef] = useState(null);
  const [showGuestsDropdown, setShowGuestsDropdown] = useState(false);
  const [guestsRef, setGuestsRef] = useState(null);
  const navigate = useNavigate();

  const labelClass = 'text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400';
  const formGridClass = compact
    ? 'grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5 items-end'
    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 items-end';
  const HERO_BG =
    'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-mikhail-nilov-8319463.jpg';

  useEffect(() => {
    if (inputRef && ((showSuggestions && locationSuggestions.length > 0) || (showHotelSuggestions && hotelSuggestions.length > 0))) {
      const rect = inputRef.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 5,
        left: rect.left,
        width: rect.width
      });
    }
  }, [showSuggestions, showHotelSuggestions, locationSuggestions.length, hotelSuggestions.length, inputRef]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle search suggestions dropdown
      if (inputRef && !inputRef.contains(event.target)) {
        const dropdown = document.querySelector('[data-dropdown="suggestions"]');
        if (dropdown && !dropdown.contains(event.target)) {
          if (showSuggestions) {
            const fakeEvent = { target: { value: locationQuery } };
            onLocationChange(fakeEvent);
          }
          if (showHotelSuggestions) {
            const fakeEvent = { target: { value: hotelQuery } };
            onHotelNameChange(fakeEvent);
          }
        }
      }

      // Handle guests dropdown
      if (guestsRef && !guestsRef.contains(event.target)) {
        const guestsDropdown = document.querySelector('[data-dropdown="guests"]');
        if (guestsDropdown && !guestsDropdown.contains(event.target)) {
          setShowGuestsDropdown(false);
        }
      }
    };

    // Only add listener when dropdown is open
    if (showSuggestions || showHotelSuggestions || showGuestsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions, showHotelSuggestions, showGuestsDropdown, inputRef, guestsRef, locationQuery, hotelQuery, onLocationChange, onHotelNameChange]);



  const searchFormInner = (
          <form onSubmit={onSubmit} className={formGridClass}>
            {/* Destination */}
            <div
              className={compact ? 'relative' : 'relative sm:col-span-2 lg:col-span-1'}
              style={{ overflow: 'visible' }}
            >
              <label className={`block mb-1 ${labelClass}`}>Destination</label>
              <div className="relative" style={{ overflow: 'visible' }}>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <input
                  ref={setInputRef}
                  type="text"
                  value={locationQuery || hotelQuery}
                  onChange={(e) => {
                    onLocationChange(e);
                    onHotelNameChange(e);
                  }}
                  placeholder="Search destinations or hotels..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 text-gray-900 placeholder-gray-400 outline-none focus:border-gray-900 transition-colors duration-200 font-poppinsLight"
                />

                {/* Combined Suggestions Dropdown */}
                {((showSuggestions && locationSuggestions.length > 0) || (showHotelSuggestions && hotelSuggestions.length > 0)) && (
                  <div
                    data-dropdown="suggestions"
                    className="absolute w-full mt-1 bg-white border border-gray-200 shadow-2xl max-h-60 overflow-y-auto"
                    style={{
                      zIndex: 1000000,
                      top: '100%',
                      left: 0,
                      right: 0
                    }}
                  >
                    {/* Combined Results */}
                    {(() => {
                      const allSuggestions = [];

                      // Add location suggestions
                      if (showSuggestions && locationSuggestions.length > 0) {
                        locationSuggestions.forEach((location, index) => {
                          // Enhanced display logic for locations with better country context
                          let displayName = '';
                          let subtitle = '';

                          if (location.isAirport) {
                            // For airports, show airport name and city, country
                            displayName = safeString(location.name || location.displayName, 'Airport');
                            subtitle = [
                              safeString(location.city),
                              safeString(location.countryName)
                            ].filter(Boolean).join(', ');
                          } else if (location.isCity) {
                            // For cities, show city, state/province, country
                            displayName = safeString(location.city, 'City');
                            subtitle = [
                              safeString(location.stateName),
                              safeString(location.countryName)
                            ].filter(Boolean).join(', ');
                          } else {
                            // For other locations (POI, RAIL, etc.), use the enhanced display
                            displayName = safeString(location.displayName || location.name || location.city, 'Location');
                            subtitle = safeString(location.fullAddress) || [
                              safeString(location.city),
                              safeString(location.stateName),
                              safeString(location.countryName)
                            ].filter(Boolean).join(', ');
                          }

                          allSuggestions.push({
                            type: 'location',
                            data: location,
                            key: `location-${index}`,
                            displayName: displayName,
                            subtitle: subtitle,
                            // Additional metadata for better sorting and display
                            isAirport: location.isAirport,
                            isCity: location.isCity,
                            isRailway: location.isRailway,
                            isPOI: location.isPOI,
                            category: location.category,
                            country: location.country,
                            countryName: location.countryName,
                            ranking: location.ranking
                          });
                        });
                      }

                      // Add hotel suggestions
                      if (showHotelSuggestions && hotelSuggestions.length > 0) {
                        hotelSuggestions.forEach((hotel, index) => {
                          allSuggestions.push({
                            type: 'hotel',
                            data: hotel,
                            key: `hotel-${index}`,
                            displayName: safeString(hotel.hotelName, hotel.name, 'Hotel Name'),
                            subtitle: hotel.city && hotel.countryName ?
                              `${safeString(hotel.city)}, ${safeString(hotel.countryName)}` :
                              safeString(hotel.countryName, hotel.country, 'Unknown Country')
                          });
                        });
                      }

                      // Enhanced sorting by relevance with country context
                      allSuggestions.sort((a, b) => {
                        const query = (locationQuery || hotelQuery || '').toLowerCase();

                        // Calculate match score for each suggestion
                        const getMatchScore = (suggestion) => {
                          const displayName = suggestion.displayName.toLowerCase();
                          const subtitle = suggestion.subtitle ? suggestion.subtitle.toLowerCase() : '';
                          const fullText = `${displayName} ${subtitle}`;

                          let score = 0;

                          // Exact match gets highest score
                          if (displayName === query) score += 100;
                          // Starts with query gets high score
                          else if (displayName.startsWith(query)) score += 80;
                          // Contains query gets medium score
                          else if (displayName.includes(query)) score += 60;
                          // Subtitle contains query gets lower score
                          else if (subtitle.includes(query)) score += 40;
                          // Full text contains query gets even lower score
                          else if (fullText.includes(query)) score += 20;

                          // Enhanced location scoring with country context
                          if (suggestion.type === 'location') {
                            // Bonus for locations with complete country information
                            if (suggestion.countryName && suggestion.country) {
                              score += 25; // Strong bonus for complete country data
                            } else if (suggestion.countryName || suggestion.country) {
                              score += 15; // Moderate bonus for partial country data
                            }

                            // Category-specific bonuses
                            if (suggestion.isCity) score += 20; // Cities are most relevant for hotel search
                            else if (suggestion.isAirport) score += 15; // Airports are also relevant
                            else if (suggestion.isPOI) score += 10; // Points of interest
                            else if (suggestion.isRailway) score += 5; // Railway stations

                            // Bonus for high-ranking locations (from Sabre API)
                            if (suggestion.ranking) {
                              const ranking = parseInt(suggestion.ranking);
                              if (ranking > 800) score += 15; // Very high ranking
                              else if (ranking > 600) score += 10; // High ranking
                              else if (ranking > 400) score += 5; // Medium ranking
                            }

                            // Bonus for simple, clear location names
                            if (displayName.split(',').length <= 2 && !displayName.includes('/')) {
                              score += 10; // Simple format like "City, Country"
                            }

                            // General location bonus
                            score += 10;
                          }

                          // Hotel scoring (existing logic)
                          if (suggestion.type === 'hotel') {
                            // Penalty for very long hotel names (likely specific venues)
                            if (displayName.length > 30) {
                              score -= 10;
                            }

                            // Bonus for hotels with country information
                            if (suggestion.data.countryName || suggestion.data.country) {
                              score += 5;
                            }
                          }

                          return score;
                        };

                        const scoreA = getMatchScore(a);
                        const scoreB = getMatchScore(b);

                        // Sort by score (highest first)
                        return scoreB - scoreA;
                      });

                      return allSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.key}
                          type="button"
                          onClick={() => {
                            if (suggestion.type === 'location') {
                              onSelectLocation(suggestion.data);
                            } else {
                              onSelectHotel(suggestion.data);
                            }
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                        >
                          <div className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {suggestion.type === 'location' ? (
                                // Different icons for different location types
                                suggestion.isAirport ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                ) : suggestion.isRailway ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                )
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              )}
                            </svg>
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">
                                {suggestion.displayName}
                              </div>
                              {suggestion.subtitle && (
                                <div className="text-sm text-gray-600">
                                  {suggestion.subtitle}
                                </div>
                              )}
                              {/* Enhanced country information display */}
                              {suggestion.type === 'location' && suggestion.country && (
                                <div className="text-xs text-gray-500 mt-1">
                                  <span className="inline-flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {suggestion.countryName || suggestion.country}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="ml-2 flex flex-col items-end space-y-1">
                              <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 text-[9px] tracking-[0.18em] uppercase font-poppinsLight text-gray-500">
                                {suggestion.type === 'hotel'
                                  ? 'Hotel'
                                  : suggestion.isAirport
                                    ? 'Airport'
                                    : suggestion.isCity
                                      ? 'City'
                                      : suggestion.isPOI
                                        ? 'POI'
                                        : suggestion.isRailway
                                          ? 'Railway'
                                          : 'Location'
                                }
                              </span>
                              {/* Show ranking for high-quality results */}
                              {suggestion.ranking && parseInt(suggestion.ranking) > 600 && (
                                <span className="text-xs text-gray-400">
                                  ★ {suggestion.ranking}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ));
                    })()}

                    {/* No results message */}
                    {((showSuggestions && locationSuggestions.length === 0) || (showHotelSuggestions && hotelSuggestions.length === 0)) &&
                      !((showSuggestions && locationSuggestions.length > 0) || (showHotelSuggestions && hotelSuggestions.length > 0)) && (
                        <div className="px-4 py-2 text-gray-500 text-center text-sm">
                          No results found
                        </div>
                      )}

                  </div>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className={compact ? '' : 'sm:col-span-2 lg:col-span-1'}>
              <label className={`block mb-1 ${labelClass}`}>Check-in & Check-out</label>
              <RangePicker
                placeholder={['Check-in', 'Check-out']}
                value={[
                  searchForm.checkInDate ? dayjs(searchForm.checkInDate) : null,
                  searchForm.checkOutDate ? dayjs(searchForm.checkOutDate) : null
                ]}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    onInputChange({
                      target: { name: 'checkInDate', value: dates[0].format('YYYY-MM-DD') }
                    });
                    onInputChange({
                      target: { name: 'checkOutDate', value: dates[1].format('YYYY-MM-DD') }
                    });
                  } else {
                    onInputChange({
                      target: { name: 'checkInDate', value: '' }
                    });
                    onInputChange({
                      target: { name: 'checkOutDate', value: '' }
                    });
                  }
                }}
                className="w-full max-w-full min-w-0 [&_.ant-picker]:max-w-full"
                style={{
                  height: '40px',
                  borderRadius: '0px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  fontSize: '13px',
                  width: '100%',
                  maxWidth: '100%'
                }}
                suffixIcon={<CalendarOutlined style={{ color: '#64748b' }} />}
                format="MMM DD, YYYY"
                disabledDate={(current) => {
                  // Disable dates before today
                  return current && current < dayjs().startOf('day');
                }}
                separator="→"
                size={compact ? 'small' : 'middle'}
              />
            </div>

            {/* Guests */}
            <div className="relative" style={{ overflow: 'visible' }}>
              <label className={`block mb-1 ${labelClass}`}>Guests</label>
              <div className="relative" style={{ overflow: 'visible' }}>
                <button
                  ref={setGuestsRef}
                  type="button"
                  onClick={() => setShowGuestsDropdown(!showGuestsDropdown)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 text-gray-900 outline-none focus:border-gray-900 transition-colors duration-200 text-left flex items-center justify-between font-poppinsLight relative"
                >
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span>
                    {searchForm.adults} {searchForm.adults === 1 ? 'adult' : 'adults'}
                    {searchForm.children > 0 && `, ${searchForm.children} ${searchForm.children === 1 ? 'child' : 'children'}`}
                  </span>
                  <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Guests Dropdown */}
                {showGuestsDropdown && (
                  <div
                    data-dropdown="guests"
                    className="absolute w-full mt-1 bg-white border border-gray-200 shadow-2xl p-4"
                    style={{
                      zIndex: 1000000,
                      top: '100%',
                      left: 0,
                      right: 0
                    }}
                  >
                    <div className="space-y-4">
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Adults</span>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              if (searchForm.adults > 1) {
                                onInputChange({
                                  target: { name: 'adults', value: (parseInt(searchForm.adults) - 1).toString() }
                                });
                              }
                            }}
                            disabled={searchForm.adults <= 1}
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="w-8 text-center font-poppinsRegular text-sm text-gray-900">{searchForm.adults}</span>
                          <button
                            type="button"
                            onClick={() => {
                              if (searchForm.adults < 6) {
                                onInputChange({
                                  target: { name: 'adults', value: (parseInt(searchForm.adults) + 1).toString() }
                                });
                              }
                            }}
                            disabled={searchForm.adults >= 6}
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Children</span>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              if (searchForm.children > 0) {
                                onInputChange({
                                  target: { name: 'children', value: (parseInt(searchForm.children) - 1).toString() }
                                });
                              }
                            }}
                            disabled={searchForm.children <= 0}
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="w-8 text-center font-poppinsRegular text-sm text-gray-900">{searchForm.children}</span>
                          <button
                            type="button"
                            onClick={() => {
                              if (searchForm.children < 4) {
                                onInputChange({
                                  target: { name: 'children', value: (parseInt(searchForm.children) + 1).toString() }
                                });
                              }
                            }}
                            disabled={searchForm.children >= 4}
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Search Button */}
            <div className={compact ? '' : 'sm:col-span-2 lg:col-span-1'}>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-2.5 bg-gray-900 text-white text-[10px] tracking-[0.28em] uppercase font-poppinsRegular hover:opacity-90 transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <LoadingSpinner />
                    <span className="ml-2 uppercase tracking-widest text-xs">Searching...</span>
                  </>
                ) : (
                  <>
                    <span>Search Hotels</span>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className={compact ? 'md:col-span-4' : 'sm:col-span-2 lg:col-span-4'}>
                <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-poppinsLight tracking-wide">
                  {error}
                </div>
              </div>
            )}
          </form>
  );

  return (
    <section
      className={
        compact
          ? 'relative z-50 px-4 py-3 transition-all duration-700 ease-out border-b border-gray-200 bg-white'
          : 'relative z-50 w-full transition-all duration-700 ease-out'
      }
      style={compact ? { boxShadow: '0 2px 12px rgba(0,0,0,0.07)' } : {}}
    >
      {!compact ? (
        <div className="relative w-full flex flex-col" style={{ minHeight: 'min(62svh, 36rem)' }}>

          {/* ── Mobile full-bleed background image ── */}
          <div className="lg:hidden absolute inset-0 z-0 overflow-hidden">
            <img
              src={HERO_BG}
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>

          {/* ── Left content panel ── */}
          <div className="relative z-10 flex flex-col w-full px-6 sm:px-10 lg:px-14 pt-8 sm:pt-10">
            <div className="lg:w-[50%]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-white/40 lg:bg-gray-300" />
                <p className="text-[9px] tracking-[0.4em] uppercase font-poppinsLight text-white/55 lg:text-gray-400">
                  Axonall Reserve · Hotel Search
                </p>
              </div>

              <h2
                className="font-AXONALLPrimeRegular uppercase leading-[1.05] tracking-wide text-white lg:text-gray-900 mb-4"
                style={{ fontSize: 'clamp(1.7rem, 3vw, 2.8rem)' }}
              >
                <span className="block">Stay Anywhere.</span>
                <span className="block">Live Exceptionally.</span>
              </h2>

              <div className="flex gap-4 mb-5">
                <div className="w-px bg-white/25 lg:bg-gray-200 shrink-0 self-stretch" />
                <p className="font-poppinsLight text-white/65 lg:text-gray-500 text-sm leading-relaxed max-w-sm">
                  Search and book world-class hotels for your clients.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  type="button"
                  onClick={() => document.getElementById('popular-destinations')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-5 py-2.5 bg-white text-gray-900 lg:bg-gray-900 lg:text-white text-[10px] tracking-[0.22em] uppercase font-poppinsRegular hover:opacity-90 transition-opacity duration-200"
                >
                  Popular Destinations
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="px-5 py-2.5 border border-white/50 text-white lg:border-gray-300 lg:text-gray-700 text-[10px] tracking-[0.22em] uppercase font-poppinsRegular hover:border-white lg:hover:border-gray-500 lg:hover:text-gray-900 transition-all duration-200"
                >
                  Dashboard
                </button>
              </div>
            </div>

            {/* Search bar — full width of left zone */}
            <div className="w-full lg:w-[65%]">
              <div
                id="search"
                className="relative bg-white border-t-2 border-t-gray-900 border-x border-b border-gray-200"
                style={{ overflow: 'visible', zIndex: 99999, position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
              >
                <div className="px-4 py-3 sm:px-5">
                  {searchFormInner}
                </div>
              </div>
            </div>
          </div>

          {/* ── Right image panel with diagonal clip (desktop only) ── */}
          <div
            className="hidden lg:block absolute top-0 right-0 bottom-0 w-[55%] overflow-hidden"
            style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)' }}
          >
            <img
              src={HERO_BG}
              alt="Luxury hotel"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-8 right-8 bg-white/90 backdrop-blur-sm p-4 border border-white/60"
              style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <p className="text-[9px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-1">Featured</p>
              <p className="font-AXONALLPrimeRegular uppercase text-gray-900 text-sm leading-tight">Curated Luxury</p>
              <p className="font-poppinsLight text-gray-500 text-[11px] mt-0.5">Handpicked hotels worldwide</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto relative z-10 px-3 sm:px-4 w-full min-w-0">
          <div
            id="search"
            className="bg-white p-3 sm:p-4 border border-gray-200 transition-all duration-700 ease-out mb-6 sm:mb-8"
            style={{ overflow: 'visible', zIndex: 99999, position: 'relative', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
          >
            {searchFormInner}
          </div>
        </div>
      )}
    </section>
  );
};

export default SearchForm;
